// # 도구 줄 — 탭 정거장은 하나, 안에서는 방향키(WAI-ARIA toolbar)
//
// 작성칸 서식 줄은 단추가 아홉, 줄의 손은 열이다. 전부 탭 정거장이면 키보드로 대화에 닿기까지 Tab 을
// 스무 번 눌러야 했다(3차 F162). 줄 안에서는 ←→·Home·End 로 움직이고, Tab 은 줄 하나를 건너뛴다.
// 마지막으로 머문 단추를 기억해 다시 들어오면 그 자리다.
//
// 단추는 여러 컴포넌트(빠른 반응·팝오버 방아쇠·메뉴 방아쇠)가 제각각 그리므로 props 로 `tabIndex` 를
// 나눠 주지 않고, 그린 뒤에 DOM 에서 한 번 고른다.

import { type FocusEvent, type KeyboardEvent, useEffect, useRef } from 'react'

/** 줄에 딸린 단추만 — 줄 안에 뜬 팝오버·메뉴의 단추는 제 키보드가 있다. */
export function tools_of(bar: HTMLElement): HTMLElement[] {
  return [...bar.querySelectorAll<HTMLButtonElement>('button')].filter((b) => {
    if (b.disabled) return false
    const shell = b.closest('[popover], dialog, [role=menu], [role=dialog]')
    return shell === null || shell.contains(bar)
  })
}

/** 방향키가 옮겨 갈 자리. 옮길 키가 아니면 `null`. 끝에서 돌아 처음으로 간다. `rtl` 이면 좌우가 뒤집힌다(G14). */
export function tool_step(key: string, at: number, count: number, rtl = false): number | null {
  if (count === 0) return null
  if (key === 'Home') return 0
  if (key === 'End') return count - 1
  const [back, forth] = rtl ? ['ArrowRight', 'ArrowLeft'] : ['ArrowLeft', 'ArrowRight']
  if (key === forth) return (at + 1) % count
  if (key === back) return (at - 1 + count) % count
  return null
}

function settle(bar: HTMLElement | null, current: HTMLElement | null): void {
  if (bar === null) return
  const all = tools_of(bar)
  const keep = current !== null && all.includes(current) ? current : all[0]
  for (const b of all) b.tabIndex = b === keep ? 0 : -1
}

/** 도구 줄 하나. 돌려준 셋을 `role="toolbar"` 요소에 그대로 편다. */
export function useToolbar<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const last = useRef<HTMLElement | null>(null)
  // 그릴 때마다 — 단추가 늦게 서거나(늦은 청크) 잠겼다 풀리면 정거장을 다시 고른다.
  useEffect(() => settle(ref.current, last.current))
  const onFocus = (e: FocusEvent<T>) => {
    const bar = ref.current
    if (bar === null || !(e.target instanceof HTMLElement)) return
    if (!tools_of(bar).includes(e.target)) return
    last.current = e.target
    settle(bar, e.target)
  }
  const onKeyDown = (e: KeyboardEvent<T>) => {
    const bar = ref.current
    if (bar === null || !(e.target instanceof HTMLElement)) return
    const all = tools_of(bar)
    const at = all.indexOf(e.target)
    if (at < 0) return
    const to = tool_step(e.key, at, all.length, getComputedStyle(bar).direction === 'rtl')
    if (to === null) return
    e.preventDefault()
    all[to]?.focus()
  }
  return { ref, onFocus, onKeyDown }
}
